import { buildDuckModel } from '../entities/Duck';
import { disposeObject3D } from '../utils/dispose';

const SAMPLE_RATE = 20;
const STORAGE_KEY = 'quackwater:best-v3';
/** Best runs saved before the rename to Quackwater. */
const LEGACY_KEY = 'ducky-racing:best-v3';
/** Numbers stored per sample: x, y, z, yaw. */
const STRIDE = 4;

type Run = { time: number; samples: number[] };

/** Records the current run and replays the best one as a see-through duck. */
export class Ghost {
  readonly group = buildDuckModel({ ghost: true });
  private best: Run | null = null;
  private recording: number[] = [];
  private recordTimer = 0;

  constructor() {
    this.group.visible = false;
    try {
      const raw = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Run;
        if (typeof parsed.time === 'number' && Array.isArray(parsed.samples)) this.best = parsed;
      }
    } catch {
      // Storage can be unavailable (private mode); the ghost is a nicety.
    }
  }

  get bestTime(): number | null {
    return this.best?.time ?? null;
  }

  startRun(): void {
    this.recording = [];
    this.recordTimer = 0;
    this.group.visible = this.best !== null;
    this.place(0);
  }

  record(delta: number, x: number, y: number, z: number, yaw: number): void {
    this.recordTimer -= delta;
    if (this.recordTimer > 0) return;
    this.recordTimer += 1 / SAMPLE_RATE;
    this.recording.push(+x.toFixed(2), +y.toFixed(2), +z.toFixed(2), +yaw.toFixed(2));
  }

  /** Returns true when this run is a new best. */
  finishRun(time: number): boolean {
    if (this.best && this.best.time <= time) return false;
    this.best = { time, samples: this.recording };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.best));
    } catch {
      // See constructor.
    }
    return true;
  }

  /** Ghost position at race time t, or null past its finish. */
  update(raceTime: number): { x: number; z: number } | null {
    if (!this.best) return null;
    return this.place(raceTime);
  }

  private place(raceTime: number): { x: number; z: number } | null {
    if (!this.best) return null;
    const s = this.best.samples;
    const frames = s.length / STRIDE;
    if (frames === 0) return null;
    const f = Math.min(raceTime * SAMPLE_RATE, frames - 1);
    const i = Math.floor(f);
    const j = Math.min(i + 1, frames - 1);
    const t = f - i;
    const a = i * STRIDE;
    const b = j * STRIDE;
    const x = s[a] + (s[b] - s[a]) * t;
    const y = s[a + 1] + (s[b + 1] - s[a + 1]) * t;
    const z = s[a + 2] + (s[b + 2] - s[a + 2]) * t;
    this.group.position.set(x, y, z);
    this.group.rotation.y = s[a + 3];
    return { x, z };
  }

  dispose(): void {
    disposeObject3D(this.group);
  }
}
